import { useCallback, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Snackbar,
	Tab,
	Tabs,
	Typography,
} from '@mui/material'
import { Business } from '@mui/icons-material'
import { useAdminData } from '../hooks/use-admin-data'
import { PageContainer } from '../components/PageContainer'
import { useAuth } from '../contexts/auth-context'
import { AdminOverviewBar } from '../components/admin/AdminOverviewBar'
import { CustomersView } from '../components/admin/CustomersView'
import { SensorsView } from '../components/admin/SensorsView'
import { MyAssignmentView } from '../components/admin/MyAssignmentView'
import { DeliveryActivityView } from '../components/admin/DeliveryActivityView'

export function AdminPortal() {
	const { customers, deliveryStats, loading, operations } = useAdminData()
	const { user, customerId, refreshAuthState, isAllScope, isAdmin } = useAuth()
	const [activeTab, setActiveTab] = useState(0)
	const [operationLoading, setOperationLoading] = useState(null)
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

	const currentAssignedCustomer = useMemo(
		() => customers.find(c => String(c.id) === String(customerId)) || null,
		[customerId, customers]
	)

	const showSnackbar = useCallback((message, severity = 'success') => {
		setSnackbar({ open: true, message, severity })
	}, [])

	const withLoading = useCallback(async (key, fn, successMsg) => {
		setOperationLoading(key)
		try {
			await fn()
			if (successMsg) showSnackbar(successMsg)
		} catch (err) {
			showSnackbar(err.message || 'An error occurred', 'error')
		} finally {
			setOperationLoading(null)
		}
	}, [showSnackbar])

	const handleCreateCustomer = useCallback((customerData) =>
		withLoading('create', () => operations.createCustomer(customerData), 'Customer created'),
	[operations, withLoading])

	const handleUpdateCustomer = useCallback((id, formData) =>
		withLoading(`update-${id}`, () => operations.updateCustomer(id, formData), 'Customer updated'),
	[operations, withLoading])

	const handleDeleteCustomer = useCallback((id) =>
		withLoading(`delete-${id}`, () => operations.deleteCustomer(id), 'Customer deleted'),
	[operations, withLoading])

	const handleToggleWebhook = useCallback((id) =>
		withLoading(`webhook-${id}`, () => operations.toggleCustomerWebhook(id)),
	[operations, withLoading])

	const handleToggleSensorDelivery = useCallback((cId, sensorId) =>
		withLoading(`sensor-${cId}-${sensorId}`, () => operations.toggleSensorDelivery(cId, sensorId)),
	[operations, withLoading])

	const handleToggleSensorRealtime = useCallback((id) =>
		withLoading(`realtime-${id}`, () => operations.toggleSensorRealtimeEnabled(id)),
	[operations, withLoading])

	const handleToggleSensorApi = useCallback((id) =>
		withLoading(`api-${id}`, () => operations.toggleSensorApiEnabled(id)),
	[operations, withLoading])

	const handleUpdateSensorVisibility = useCallback((id, data) =>
		withLoading(null, () => operations.updateSensorVisibility(id, data)),
	[operations, withLoading])

	const handleCreateSensor = useCallback((customerId, data) =>
		withLoading(`create-sensor-${customerId}`, () => operations.createSensor(customerId, data), 'Sensor added'),
	[operations, withLoading])

	const handleUpdateSensor = useCallback((id, data) =>
		withLoading(`update-sensor-${id}`, () => operations.updateSensor(id, data), 'Sensor updated'),
	[operations, withLoading])

	const handleDeleteSensor = useCallback((id) =>
		withLoading(`delete-sensor-${id}`, () => operations.deleteSensor(id), 'Sensor deleted'),
	[operations, withLoading])

	const handleTransferSensor = useCallback((id, targetCustomerId) =>
		withLoading(`transfer-${id}`, () => operations.transferSensor(id, targetCustomerId), 'Sensor transferred'),
	[operations, withLoading])

	const handleAssign = useCallback(async (selectedCustomerId) => {
		const parsedId = Number(selectedCustomerId)
		const assignId = Number.isNaN(parsedId) ? selectedCustomerId : parsedId
		setOperationLoading('assign-current-user')
		try {
			await operations.assignCurrentUserToCustomer(user.id, assignId, user.email)
			await refreshAuthState()
			showSnackbar('Customer assignment updated')
		} finally {
			setOperationLoading(null)
		}
	}, [operations, user, refreshAuthState, showSnackbar])

	if (loading) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<CircularProgress />
				</Box>
			</PageContainer>
		)
	}

	const tabs = [
		{ label: 'Customers', count: customers.length },
		{ label: 'Sensors', count: customers.reduce((n, c) => n + (c.sensor_configs?.length || 0), 0) },
		{ label: 'My Assignment' },
		{ label: 'Delivery Activity', count: deliveryStats?.recentDeliveries?.length || 0 },
	]

	return (
		<PageContainer>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
				<Box>
					<Typography variant="h4" fontWeight="bold" gutterBottom>
						Admin Portal
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Manage customers, sensors, and platform configuration
					</Typography>
				</Box>
				{isAdmin && (isAllScope || currentAssignedCustomer) && (
					<Chip
						icon={<Business fontSize="small" />}
						label={isAllScope ? 'Viewing as: All Customers' : `Viewing as: ${currentAssignedCustomer.name}`}
						variant="outlined"
						color={isAllScope ? 'primary' : 'default'}
						size="small"
					/>
				)}
			</Box>

			<AdminOverviewBar deliveryStats={deliveryStats} customerCount={customers.length} />

			<Box sx={{ borderBottom: '1px solid', borderColor: 'dividerSecondary', mb: 3 }}>
				<Tabs
					value={activeTab}
					onChange={(_, v) => setActiveTab(v)}
					sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minWidth: 'auto', px: 2 } }}
				>
					{tabs.map((tab, i) => (
						<Tab
							key={i}
							label={
								tab.count !== undefined ? (
									<Box display="flex" alignItems="center" gap={1}>
										{tab.label}
										<Chip label={tab.count} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
									</Box>
								) : tab.label
							}
						/>
					))}
				</Tabs>
			</Box>

			{activeTab === 0 && (
			<CustomersView
				customers={customers}
				operationLoading={operationLoading}
				onCreateCustomer={handleCreateCustomer}
				onUpdateCustomer={handleUpdateCustomer}
				onDeleteCustomer={handleDeleteCustomer}
				onToggleWebhook={handleToggleWebhook}
				onToggleSensorDelivery={handleToggleSensorDelivery}
				onToggleSensorRealtimeEnabled={handleToggleSensorRealtime}
				onToggleSensorApiEnabled={handleToggleSensorApi}
				onUpdateSensorVisibility={handleUpdateSensorVisibility}
			onCreateSensor={handleCreateSensor}
			onUpdateSensor={handleUpdateSensor}
			onDeleteSensor={handleDeleteSensor}
			onTransferSensor={handleTransferSensor}
			currentAssignedCustomerId={customerId}
				refreshAuthState={refreshAuthState}
			/>
			)}
			{activeTab === 1 && (
			<SensorsView
				customers={customers}
				operationLoading={operationLoading}
				onUpdateSensor={handleUpdateSensor}
				onTransferSensor={handleTransferSensor}
				onDeleteSensor={handleDeleteSensor}
				onToggleSensorDelivery={handleToggleSensorDelivery}
				onToggleSensorRealtimeEnabled={handleToggleSensorRealtime}
				onToggleSensorApiEnabled={handleToggleSensorApi}
				onUpdateSensorVisibility={handleUpdateSensorVisibility}
			/>
			)}
			{activeTab === 2 && (
				<MyAssignmentView
					user={user}
					customerId={customerId}
					customers={customers}
					onAssign={handleAssign}
					operationLoading={operationLoading}
				/>
			)}
			{activeTab === 3 && (
				<DeliveryActivityView deliveryStats={deliveryStats} />
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar(s => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar(s => ({ ...s, open: false }))}
					variant="filled"
					sx={{ minWidth: 280 }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</PageContainer>
	)
}
